import React, { useEffect, useMemo, useRef } from 'react'
import { Box, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import StorefrontIcon from '@mui/icons-material/Storefront'
import FilterListIcon from '@mui/icons-material/FilterList'
import { Transition } from 'react-transition-group'
import { useBreweryActionBar } from '../../state/hooks/useBreweryActionBar'
import { useBreweryDetail } from '../../state/hooks/useBreweryDetail'
import { useFilters } from '../../state/hooks/useFilters'
import { subscribeToSelection } from '../BeerCard/BeerCardContainer'
import styles from '../SelectionActionBar/SelectionActionBar.module.css'

interface BreweryActionBarProps {
  useThreeButtonSizing?: boolean
}

const BreweryActionBar: React.FC<BreweryActionBarProps> = ({ useThreeButtonSizing = false }) => {
  const { isOpen, breweryName, closeBreweryActions } = useBreweryActionBar()
  const { openBreweryDetail } = useBreweryDetail()
  const { filters, setBreweryFilter } = useFilters()
  const previousSelectionRef = useRef<Set<string> | null>(null)
  const actionBarRef = useRef<HTMLDivElement | null>(null)

  const transitionRef = useRef<HTMLDivElement | null>(null)

  const setRootRef = (node: HTMLDivElement | null) => {
    transitionRef.current = node
    actionBarRef.current = node
  }

  const flipDurationMs = 240
  const defaultFlipStyle: React.CSSProperties = {
    transition: `transform ${flipDurationMs}ms cubic-bezier(0.2, 0.9, 0.2, 1), opacity ${flipDurationMs}ms ease`,
    opacity: 0,
    transform: 'perspective(1200px) rotateX(75deg) translateY(12px) scale(0.98)',
    transformOrigin: '50% 100%',
    backfaceVisibility: 'hidden',
    willChange: 'transform, opacity',
  }

  const transitionFlipStyles: Record<string, React.CSSProperties> = {
    entering: {
      opacity: 1,
      transform: 'perspective(1200px) rotateX(0deg) translateY(0) scale(1)',
    },
    entered: {
      opacity: 1,
      transform: 'perspective(1200px) rotateX(0deg) translateY(0) scale(1)',
    },
    exiting: {
      opacity: 0,
      transform: 'perspective(1200px) rotateX(75deg) translateY(12px) scale(0.98)',
    },
    exited: {
      opacity: 0,
      transform: 'perspective(1200px) rotateX(75deg) translateY(12px) scale(0.98)',
      pointerEvents: 'none',
    },
  }

  useEffect(() => {
    if (!isOpen) {
      previousSelectionRef.current = null
      return
    }

    const setsEqual = (a: Set<string>, b: Set<string>): boolean => {
      if (a.size !== b.size) return false

      let allMatch = true
      a.forEach((value) => {
        if (!b.has(value)) {
          allMatch = false
        }
      })

      return allMatch
    }

    const unsubscribe = subscribeToSelection((ids) => {
      if (!previousSelectionRef.current) {
        previousSelectionRef.current = ids
        return
      }

      const didChange = !setsEqual(previousSelectionRef.current, ids)
      if (didChange) {
        closeBreweryActions()
      }
    })

    return () => {
      unsubscribe()
      previousSelectionRef.current = null
    }
  }, [closeBreweryActions, isOpen])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null
      if (!target) {
        return
      }

      const root = actionBarRef.current
      if (root && root.contains(target)) {
        return
      }

      closeBreweryActions()
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [closeBreweryActions, isOpen])

  const isActiveFilter = useMemo(() => {
    if (!breweryName) return false
    return filters.breweryFilter === breweryName
  }, [filters.breweryFilter, breweryName])

  if (!isOpen || !breweryName) {
    return null
  }

  const handleOpenBreweryPage = () => {
    const didOpen = openBreweryDetail(breweryName)
    if (!didOpen) {
      setBreweryFilter(breweryName)
    }
    closeBreweryActions()
  }

  const handleFilterByBrewery = () => {
    if (isActiveFilter) {
      setBreweryFilter('')
    } else {
      setBreweryFilter(breweryName)
    }
    closeBreweryActions()
  }

  return (
    <Transition
      in={isOpen}
      nodeRef={transitionRef}
      timeout={flipDurationMs}
      mountOnEnter
      unmountOnExit
    >
      {(state) => (
        <Box
          ref={setRootRef}
          role="region"
          aria-label="Brewery actions"
          className={`${styles.actionBar} ${styles.actionBarCompact}`}
          sx={{
            zIndex: 1005,
            bottom: '72px',
            paddingLeft: '16px',
            paddingRight: '16px',
          }}
          style={{
            ...defaultFlipStyle,
            ...transitionFlipStyles[state],
          }}
        >
          <Box className={`${styles.content} ${styles.contentCompact}`}>
            <IconButton
              onClick={closeBreweryActions}
              className={`${styles.closeButton} ${styles.closeButtonCompact}`}
              aria-label="Close brewery actions"
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.72)',
                color: 'var(--primary-colour)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.88)',
                  color: 'var(--primary-colour)',
                },
              }}
            >
              <CloseIcon sx={{ fontSize: '1.65rem' }} />
            </IconButton>

            <Box className={`${styles.actions} ${useThreeButtonSizing ? styles.actionsThree : ''} ${styles.actionsCompact}`}>
              <button
                onClick={handleOpenBreweryPage}
                className={`${styles.actionButton} ${styles.actionButtonCompact}`}
                aria-label="Open brewery page"
              >
                <StorefrontIcon className={`${styles.actionIcon} ${styles.actionIconCompact}`} />
                <span className={styles.actionText}>Brewery Page</span>
              </button>

              <button
                onClick={handleFilterByBrewery}
                className={`${styles.actionButton} ${styles.actionButtonCompact}`}
                aria-label={isActiveFilter ? 'Clear brewery filter' : 'Filter by brewery'}
              >
                <FilterListIcon className={`${styles.actionIcon} ${styles.actionIconCompact}`} />
                <span className={styles.actionText}>{isActiveFilter ? 'Clear Filter' : 'Filter Brewery'}</span>
              </button>
            </Box>
          </Box>
        </Box>
      )}
    </Transition>
  )
}

export default BreweryActionBar
